import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotifierService } from 'angular-notifier';
import { BoobleModalService } from 'booble-modal';
import { OrdersService } from 'src/app/shared/services/orders.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-send-contract',
  templateUrl: './send-contract.component.html',
  styleUrls: ['./send-contract.component.scss'],
})
export class SendContractComponent implements OnInit {
  boobleModalData: any;
  loading: boolean;
  private readonly notifier: NotifierService;
  formSettlement!: FormGroup;
  formSettlementInbound!: FormGroup;
  private destroy$ = new Subject<void>();

  constructor(
    private modalService: BoobleModalService,
    notifierService: NotifierService,
    formBuilder: FormBuilder,
    private readonly ordersService: OrdersService
  ) {
    this.notifier = notifierService;

    this.formSettlement = formBuilder.group(
      {
        contractSettlementNumber: ['', Validators.pattern('^[0-9]*$')],
        exchangeContractFile: [null],
        proofOperationFile: [null, Validators.required],
        proofOperationLater: [false],
        sendEmail: [true],
      },
      { validators: this.validationForm.bind(this) }
    );

    this.formSettlementInbound = formBuilder.group({
      contractSettlementNumber: [
        '',
        [Validators.required, Validators.pattern('^[0-9]*$')],
      ],
      exchangeContractFile: [null, Validators.required],
      sendEmail: [true],
    });
  }

  get isResend(): boolean {
    return !!this.boobleModalData?.orderSettlement?.exchangeContractFile;
  }

  get settlement(): any {
    return this.formSettlement.value;
  }

  get settlementInbound(): any {
    return this.formSettlementInbound.value;
  }

  getFileName(value: any): string {
    if (!value) return '';
    if (value instanceof File) {
      return value.name;
    }
    if (typeof value === 'string') {
      const parts = value.split('-');
      if (parts.length > 5) {
        return parts.slice(5).join('-');
      }
      return value;
    }
    return value?.name || value;
  }

  ngOnInit() {
    if (typeof this.boobleModalData === 'number') {
      this.loading = true;
      this.ordersService
        .getOrderById(this.boobleModalData, "false")
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.loading = false;
            this.boobleModalData = result.data;
            this.initializeComponent();
          },
          error: (err) => {
            this.loading = false;
          },
        });
    } else {
      this.initializeComponent();
    }
  }

  initializeComponent(): void {
    this.patchFormWithOrderSettlement();
    this.adjustValidatorsForStatusFlow();
  }

  adjustValidatorsForStatusFlow(): void {
    const statusFlow = this.boobleModalData?.statusFlow;
    const isAvailableOrIdentifying =
      statusFlow === 'AVAILABLE_PAYMENT' || statusFlow === 'IDENTIFYING_PAYMENT';

    if (isAvailableOrIdentifying && this.boobleModalData?.remmitanceType === 'OUTBOUND') {
      this.formSettlement.get('proofOperationFile')?.clearValidators();
      this.formSettlement.get('proofOperationFile')?.updateValueAndValidity();
      this.formSettlement.get('proofOperationLater')?.setValue(true);
    }
  }

  closeModal() {
    this.modalService.dismiss(null);
  }

  proofOperationFileChange(formGroup: FormGroup, event: Event) {
    const file = (event.target as HTMLInputElement).files[0];
    formGroup.patchValue({ proofOperationFile: file });
  }

  exchangeContractFileChange(formGroup: FormGroup, event: Event) {
    const file = (event.target as HTMLInputElement).files[0];
    formGroup.patchValue({ exchangeContractFile: file });
  }

  patchFormWithOrderSettlement(): void {
    const orderSettlement = this.boobleModalData?.orderSettlement;
    if (!orderSettlement) return;

    const {
      contractSettlementNumber,
      exchangeContractFile,
      proofOperationFile,
    } = orderSettlement;

    if (this.boobleModalData?.remmitanceType === 'INBOUND') {
      this.formSettlementInbound.patchValue({
        contractSettlementNumber,
        exchangeContractFile,
        sendEmail: true,
      });
    } else if (this.boobleModalData?.remmitanceType === 'OUTBOUND') {
      this.formSettlement.patchValue({
        contractSettlementNumber,
        exchangeContractFile,
        proofOperationFile,
        proofOperationLater: !!proofOperationFile,
        sendEmail: true,
      });
    }
  }

  validationForm(formGroup: FormGroup): { [key: string]: any } | null {
    const proofOperationLater = formGroup.get('proofOperationLater')?.value;
    const proofOperationFile = formGroup.get('proofOperationFile')?.value;
    const contractSettlementNumber = formGroup.get(
      'contractSettlementNumber'
    )?.value;
    const exchangeContractFile = formGroup.get('exchangeContractFile')?.value;

    const statusFlow = this.boobleModalData?.statusFlow;
    const isAvailableOrIdentifying =
      statusFlow === 'AVAILABLE_PAYMENT' || statusFlow === 'IDENTIFYING_PAYMENT';

    let isValid = true;

    if (isAvailableOrIdentifying) {
      formGroup.get('proofOperationFile')?.setErrors(null);

      if (!contractSettlementNumber) {
        formGroup.get('contractSettlementNumber')?.setErrors({ required: true });
        isValid = false;
      } else {
        formGroup.get('contractSettlementNumber')?.setErrors(null);
      }

      if (!exchangeContractFile) {
        formGroup.get('exchangeContractFile')?.setErrors({ required: true });
        isValid = false;
      } else {
        formGroup.get('exchangeContractFile')?.setErrors(null);
      }

      return isValid ? null : { formInvalid: true };
    }

    if (proofOperationLater) {
      if (!contractSettlementNumber) {
        formGroup
          .get('contractSettlementNumber')
          ?.setErrors({ required: true });
        isValid = false;
      }

      if (!exchangeContractFile) {
        formGroup.get('exchangeContractFile')?.setErrors({ required: true });
        isValid = false;
      }

      formGroup.get('proofOperationFile')?.setErrors(null);
    } else {
      if (!proofOperationFile) {
        formGroup.get('proofOperationFile')?.setErrors({ required: true });
        isValid = false;
      } else {
        formGroup.get('proofOperationFile')?.setErrors(null);
      }

      formGroup.get('contractSettlementNumber')?.setErrors(null);
      formGroup.get('exchangeContractFile')?.setErrors(null);
    }

    return isValid ? null : { formInvalid: true };
  }

  resetControl(controlName: string) {
    this.formSettlement.get(controlName)?.reset();
    this.formSettlement.get(controlName)?.setValue(null);
    this.formSettlement.get(controlName)?.markAsPristine();
    this.formSettlement.get(controlName)?.markAsUntouched();
  }

  resetExchangeContractFile() {
    this.formSettlementInbound.get('exchangeContractFile')?.reset();
    this.formSettlementInbound.get('exchangeContractFile')?.setValue(null);
    this.formSettlementInbound.get('exchangeContractFile')?.markAsPristine();
    this.formSettlementInbound.get('exchangeContractFile')?.markAsUntouched();
  }

  settlementConfirm() {
    this.loading = true;

    const statusFlow = this.boobleModalData?.statusFlow;
    const isAvailableOrIdentifying =
      statusFlow === 'AVAILABLE_PAYMENT' || statusFlow === 'IDENTIFYING_PAYMENT';

    const formData = new FormData();

    if (this.boobleModalData.remmitanceType === 'INBOUND') {
      formData.append(
        'ContractSettlementNumber',
        this.settlementInbound?.contractSettlementNumber
      );

      if (this.settlementInbound?.exchangeContractFile instanceof File) {
        formData.append(
          'ExchangeContract',
          this.settlementInbound.exchangeContractFile
        );
      }

      formData.append('SendEmail', this.settlementInbound?.sendEmail);
    } else if (this.boobleModalData?.remmitanceType === 'OUTBOUND') {
      formData.append(
        'ContractSettlementNumber',
        this.settlement?.contractSettlementNumber
      );

      if (this.settlement?.exchangeContractFile instanceof File) {
        formData.append(
          'ExchangeContract',
          this.settlement.exchangeContractFile
        );
      }

      if (this.settlement?.proofOperationFile instanceof File) {
        formData.append('ProofOperation', this.settlement.proofOperationFile);
      }

      formData.append(
        'proofOperationLater',
        this.settlement?.proofOperationLater
      );
      formData.append('SendEmail', this.settlement?.sendEmail);
    }

    const request$ = isAvailableOrIdentifying && this.boobleModalData?.remmitanceType === 'OUTBOUND'
      ? this.ordersService.uploadOrderAttachments(this.boobleModalData?.id, formData)
      : this.ordersService.seetlementConfirm(this.boobleModalData?.id, formData);

    request$.subscribe({
        next: () => {
          this.loading = false;
          this.closeModal();
          this.notifier.notify(
            'success',
            isAvailableOrIdentifying && this.boobleModalData?.remmitanceType === 'OUTBOUND'
              ? 'Contrato de câmbio enviado com sucesso.'
              : 'Confirmação de que a ordem foi liquidada realizada com sucesso.'
          );
          this.ordersService.updateOrderView();
        },
        error: (err) => {
          this.loading = false;
          console.error('Erro ao confirmar liquidação:', err);
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
